import {
  CAMINHO_DA_PONTE,
  linkDeWhatsApp,
  mensagemDaPonte,
  montarLinkDeReserva,
  validarTelefone,
  type ExtraDaPonte,
} from '@locaveri/shared'
import { Copy, ExternalLink } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { env } from '@/lib/env'

/**
 * MANDAR O LINK PARA O CLIENTE — a metade do painel na ponte do WhatsApp
 * (Gap 13).
 *
 * ===========================================================================
 * O PROBLEMA QUE ISTO RESOLVE É DE PRODUTO, NÃO DE TELA
 * ===========================================================================
 * A operação fecha reserva por WhatsApp, e vai continuar fechando: é o caminho
 * de menor atrito PARA O ATENDENTE. Um app impecável não muda isso sozinho — e
 * enquanto não mudar, a base de dados que a plataforma existe para construir
 * nasce furada.
 *
 * A saída não é proibir o WhatsApp. É fazer com que, no meio da conversa, o
 * caminho mais curto para o atendente seja mandar UM link que já leva tudo o
 * que ele acabou de cotar. O cliente toca, confirma, cadastra e paga — dentro
 * do sistema. O dado entra porque o caminho fácil passou a ser o de dentro.
 *
 * ===========================================================================
 * O LINK NÃO CARREGA PREÇO
 * ===========================================================================
 * Ele carrega ESCOLHAS — categoria, período, locais, opcionais, cupom. Quanto
 * custa continua saindo do motor, na cotação que o funil vai refazer. É por
 * isso que a mensagem mostra o total como TEXTO, e não como parâmetro: se o
 * cliente demorar e a cotação vencer, o funil recota e mostra o preço novo, e a
 * mensagem antiga não vira promessa que o sistema não honra.
 */

export interface DadosDoLinkParaOCliente {
  readonly categoriaSlug: string | null
  readonly categoriaNome: string
  /** `AAAA-MM-DD` — a data civil, sem hora. */
  readonly retirada: string | null
  readonly devolucao: string | null
  /** `HH:MM`, hora de parede. */
  readonly horaDeRetirada: string | null
  readonly horaDeDevolucao: string | null
  readonly retiradaLocalId: string | null
  readonly devolucaoLocalId: string | null
  readonly extras: readonly ExtraDaPonte[]
  readonly cupom: string | null
  /** Só para a mensagem — o link não carrega valor nenhum. Ver o cabeçalho. */
  readonly periodoEmTexto: string | null
  readonly totalEmTexto: string | null
  readonly nomeDoCliente: string | null
  readonly telefoneDoCliente: string | null
}

interface Props {
  readonly dados: DadosDoLinkParaOCliente | null
  readonly aoFechar: () => void
}

/**
 * O link de desenvolvimento não abre em aparelho nenhum.
 *
 * Mandar `http://localhost:5192/reservar/...` por WhatsApp é uma falha
 * silenciosa perfeita: o painel funciona, o link é gerado, o atendente manda, e
 * o cliente vê uma página que não carrega. Quem descobre é o cliente.
 */
function ehEnderecoLocal(base: string): boolean {
  try {
    const { hostname } = new URL(base)
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')
  } catch {
    return false
  }
}

export function DialogoDeLinkParaOCliente({ dados, aoFechar }: Props): ReactElement {
  const [telefone, setTelefone] = useState('')
  const [mensagemEditada, setMensagemEditada] = useState<string | null>(null)

  // O telefone da conta é o ponto de partida, e o campo continua editável: quem
  // está no telefone com o cliente costuma saber o número melhor que o cadastro.
  const telefoneEfetivo = telefone === '' ? (dados?.telefoneDoCliente ?? '') : telefone

  const link = useMemo(() => {
    if (dados === null) {
      return ''
    }

    return montarLinkDeReserva(env.VITE_SITE_URL, CAMINHO_DA_PONTE, {
      categoriaSlug: dados.categoriaSlug,
      retirada: dados.retirada,
      devolucao: dados.devolucao,
      horaDeRetirada: dados.horaDeRetirada,
      horaDeDevolucao: dados.horaDeDevolucao,
      retiradaLocalId: dados.retiradaLocalId,
      devolucaoLocalId: dados.devolucaoLocalId,
      extras: dados.extras,
      cupom: dados.cupom,
    })
  }, [dados])

  const mensagemPadrao = useMemo(() => {
    if (dados === null) {
      return ''
    }

    return mensagemDaPonte({
      nomeDoCliente: dados.nomeDoCliente,
      categoria: dados.categoriaNome,
      periodo: dados.periodoEmTexto,
      total: dados.totalEmTexto,
      link,
    })
  }, [dados, link])

  const mensagem = mensagemEditada ?? mensagemPadrao
  const linkDoWhatsApp = linkDeWhatsApp(telefoneEfetivo, mensagem)
  const local = ehEnderecoLocal(env.VITE_SITE_URL)

  function fechar(): void {
    setTelefone('')
    setMensagemEditada(null)
    aoFechar()
  }

  async function copiar(texto: string, oQue: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(texto)
      toast.success(`${oQue} copiado.`)
    } catch {
      // `navigator.clipboard` exige contexto seguro (https ou localhost) e
      // permissão. Falhar em silêncio deixaria o atendente achando que copiou.
      toast.error('Não consegui copiar. Selecione o texto e copie à mão.')
    }
  }

  return (
    <Dialog
      open={dados !== null}
      onOpenChange={(abrir) => {
        if (!abrir) {
          fechar()
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mandar o link para o cliente</DialogTitle>
          <DialogDescription>
            O cliente abre e cai na reserva com tudo já escolhido — faltando só confirmar, cadastrar
            e pagar. O preço é recalculado na hora; o link não carrega valor.
          </DialogDescription>
        </DialogHeader>

        {local ? (
          <Alert variant="warning">
            <AlertTitle>Este link não vai abrir no celular do cliente</AlertTitle>
            <AlertDescription>
              O painel está configurado com um endereço de desenvolvimento ({env.VITE_SITE_URL}).
              Quem cuida do ambiente precisa apontar <code>VITE_SITE_URL</code> para o domínio de
              produção.
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-4">
          <Campo rotulo="WhatsApp do cliente" dica="Com DDD. É só para abrir a conversa.">
            {(controle) => (
              <Input
                id={controle.id}
                inputMode="tel"
                placeholder="(11) 98888-7777"
                value={telefoneEfetivo}
                onChange={(evento) => {
                  setTelefone(evento.target.value)
                }}
              />
            )}
          </Campo>

          <Campo
            rotulo="Mensagem"
            dica="Dá para editar antes de mandar — o WhatsApp abre com este texto."
          >
            {(controle) => (
              <Textarea
                id={controle.id}
                rows={8}
                value={mensagem}
                onChange={(evento) => {
                  setMensagemEditada(evento.target.value)
                }}
              />
            )}
          </Campo>

          <div>
            <p className="text-xs text-fg-muted">O link, se preferir colar em outro lugar:</p>
            <p className="mt-1 break-all rounded-md border border-border bg-elevated p-2 text-xs text-fg-secondary">
              {link}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={fechar}>
            Fechar
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              void copiar(link, 'Link')
            }}
          >
            <Copy aria-hidden="true" />
            Copiar link
          </Button>
          <Button
            disabled={linkDoWhatsApp === null}
            // O motivo fica no título porque um botão desabilitado sem
            // explicação é o mesmo que um botão quebrado.
            title={
              linkDoWhatsApp === null
                ? 'Informe um telefone válido, com DDD, para abrir o WhatsApp.'
                : undefined
            }
            onClick={() => {
              if (linkDoWhatsApp === null) {
                return
              }
              // `noopener` junto: sem ele a aba aberta ganha referência ao
              // `window` do painel, que é uma sessão autenticada.
              window.open(linkDoWhatsApp, '_blank', 'noopener,noreferrer')
            }}
          >
            <ExternalLink aria-hidden="true" />
            Abrir no WhatsApp
          </Button>
        </DialogFooter>

        {telefoneEfetivo !== '' && !validarTelefone(telefoneEfetivo) ? (
          <p className="text-xs text-danger-fg">
            Telefone inválido — confira o DDD. Você ainda pode copiar o link e mandar por outro
            canal.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
